import { apiService } from "api/apiService";
import {
  backfillMeetingNames,
  ratingHistoryFromDto,
  studentMeetingFromDto,
} from "meetings/student-meeting/api/mappers";
import type {
  RatingHistoryItem,
  RatingHistoryItemDto,
  RatingTextField,
  StudentMeeting,
  StudentMeetingDto,
  StudentMeetingRating,
} from "meetings/student-meeting/types";

/**
 * Loads a single student meeting with its lessongroups, scoped to the
 * logged-in teacher. Rejects (404) when the meeting does not exist for this
 * teacher — the store/page surface that as an error state.
 *
 * Staff roles (teamleader/organizer/manager/webmaster) may pass a
 * `teacherShortage` to inspect another teacher's board instead.
 */
export const fetchStudentMeeting = async (
  meetingId: number,
  teacherShortage?: string,
): Promise<StudentMeeting> => {
  const { data } = await apiService.get<StudentMeetingDto>(
    `/api/teacher/meetings/${meetingId}`,
    teacherShortage ? { params: { shortage: teacherShortage } } : undefined,
  );

  return studentMeetingFromDto(data);
};

/**
 * Autosaves a single text field (feedback `remark` or feedforward `action`)
 * for a rating.
 */
export const saveRatingText = async (
  studentId: number,
  ratingId: number,
  type: RatingTextField,
  text: string,
): Promise<void> => {
  await apiService.put(
    `/api/student/${studentId}/ratings/${ratingId}`,
    { rating: JSON.stringify({ id: ratingId, type, text }) },
    { params: { textonly: "1" } },
  );
};

/** Persists the rating value (1-4, null = none) for a rating row. */
export const saveRatingValue = async (
  rating: StudentMeetingRating,
): Promise<void> => {
  await apiService.put(
    `/api/student/${rating.studentId}/ratings/${rating.id}`,
    {
      rating_type: rating.rating !== null ? String(rating.rating) : null,
    },
  );
};

/** Fetches the historical ratings for a single rating row. */
export const fetchRatingHistory = async (
  ratingId: number,
): Promise<RatingHistoryItem[]> => {
  const { data } = await apiService.get<RatingHistoryItemDto[]>(
    `/api/rating-history/${ratingId}`,
  );
  return backfillMeetingNames(data.map(ratingHistoryFromDto));
};
